import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './dialog';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { SignatureCanvas } from './SignatureCanvas';
import { Upload, X, Check, PenTool, Type, Image } from 'lucide-react';
import { cn } from '../../lib/utils';

interface SignatureDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (signature: string, name: string, comment?: string, stampImage?: string) => void;
  title?: string;
  initialSignature?: string | null;
  initialStampImage?: string | null;
  initialName?: string;
  initialComment?: string;
}

export const SignatureDialog: React.FC<SignatureDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  title = "Add Signature",
  initialSignature = null,
  initialStampImage = null,
  initialName = "",
  initialComment = ""
}) => {
  const [activeTab, setActiveTab] = useState<'draw' | 'upload' | 'text'>('draw');
  const [signature, setSignature] = useState<string | null>(initialSignature);
  const [name, setName] = useState(initialName);
  const [comment, setComment] = useState(initialComment);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [stampImage, setStampImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stampImageInputRef = useRef<HTMLInputElement>(null);
  /** Avoid re-running "fresh open" reset when initialName etc. update while the dialog stays open (was clearing stamp/signature). */
  const hasOpenedThisSessionRef = useRef(false);

  // Keep name in sync when team list / selection loads after the dialog is already open
  React.useEffect(() => {
    if (isOpen) {
      setName(initialName);
    }
  }, [isOpen, initialName]);

  // Full reset only on closed → open (not on every initialName change while open)
  React.useEffect(() => {
    if (!isOpen) {
      hasOpenedThisSessionRef.current = false;
      return;
    }
    if (hasOpenedThisSessionRef.current) {
      return;
    }
    hasOpenedThisSessionRef.current = true;

    setComment(initialComment);
    const sig = initialSignature ?? null;
    setSignature(sig);
    setUploadedImage(sig);
    const stamp =
      initialStampImage && String(initialStampImage).trim() ? initialStampImage : null;
    setStampImage(stamp);
    setName(initialName);
    setActiveTab('draw');
    setIsDragOver(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (stampImageInputRef.current) {
      stampImageInputRef.current.value = '';
    }
  }, [isOpen, initialComment, initialSignature, initialStampImage, initialName]);

  const processFile = (file: File) => {
    setIsUploading(true);
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      setIsUploading(false);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      setIsUploading(false);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setUploadedImage(result);
      setSignature(result);
      setIsUploading(false);
    };
    reader.onerror = () => {
      alert('Error reading file');
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const processStampImage = (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setStampImage(result);
    };
    reader.onerror = () => {
      alert('Error reading file');
    };
    reader.readAsDataURL(file);
  };

  const handleStampImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processStampImage(file);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      processFile(file);
    }
  };

  const handleSave = () => {
    if (!signature || !name.trim()) {
      alert('Please provide both signature and name');
      return;
    }

    onSave(signature, name.trim(), comment.trim() || undefined, stampImage || undefined);
    handleClose();
  };

  const handleClose = () => {
    setSignature(initialSignature);
    setName(initialName);
    setComment(initialComment);
    setUploadedImage(initialSignature);
    setStampImage(null);
    setIsDragOver(false);
    setActiveTab('draw');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (stampImageInputRef.current) {
      stampImageInputRef.current.value = '';
    }
    onClose();
  };

  const handleSignatureChange = (signatureData: string | null) => {
    setSignature(signatureData);
  };

  const generateTextSignature = (text: string): string => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    // Set canvas size
    canvas.width = 400;
    canvas.height = 120;

    // Set background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Set text style
    ctx.font = 'italic 32px "Brush Script MT", "Lucida Handwriting", cursive';
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Add some styling effects
    ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;

    // Draw the text
    ctx.fillText(text, canvas.width / 2, canvas.height / 2);

    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    return canvas.toDataURL('image/png');
  };

  const handleNameChange = (newName: string) => {
    setName(newName);
    if (activeTab === 'text') {
      if (newName.trim()) {
        const textSignature = generateTextSignature(newName.trim());
        setSignature(textSignature);
      } else {
        setSignature(null);
      }
    }
  };

  const handleTabChange = (tab: 'draw' | 'upload' | 'text') => {
    setActiveTab(tab);
    if (tab === 'text' && name.trim()) {
      const textSignature = generateTextSignature(name.trim());
      setSignature(textSignature);
    } else if (tab === 'draw') {
      // Clear signature when switching to draw tab to start with clean canvas
      setSignature(null);
      setUploadedImage(null);
    }
  };

  const isSaveDisabled = !signature || !name.trim();

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Name Input */}
          <div className="space-y-2">
            <Label htmlFor="signature-name">Name *</Label>
            <Input
              id="signature-name"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Enter your name"
              className="w-full"
            />
          </div>

          {/* Comment Input */}
          {/* <div className="space-y-2">
            <Label htmlFor="signature-comment">Comment (Optional)</Label>
            <Input
              id="signature-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment..."
              className="w-full"
            />
          </div> */}

          {/* Tab Navigation */}
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
            <Button
              variant= {activeTab === 'draw' ? "default" : "outline"}
              onClick={() => handleTabChange('draw')}
              className={cn(
                "flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors",
                
              )}
            >
              <PenTool className="h-4 w-4 mr-2" />
              Draw
            </Button>
            <Button
              variant= {activeTab === 'text' ? "default" : "outline"}
                onClick={() => handleTabChange('text')}
              className={cn(
                "flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors",
               
              )}
            >
              <Type className="h-4 w-4 mr-2" />
              Text
            </Button>
            <Button
              variant= {activeTab === 'upload' ? "default" : "outline"}
              onClick={() => handleTabChange('upload')}
              className={cn(
                "flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors",
              
            )}>
              <Image className="h-4 w-4 mr-2" />
              Upload
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => stampImageInputRef.current?.click()}
              className={cn(
                "flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors",
              
            )}>
              <Image className="h-4 w-4 mr-2" />
              Upload stamped image
            </Button>
            <input
              ref={stampImageInputRef}
              type="file"
              accept="image/*"
              onChange={handleStampImageUpload}
              className="hidden"
              id="stamp-image-upload"
              aria-label="Upload stamped image"
            />
          </div>

          {/* Tab Content */}
          {activeTab === 'draw' ? (
            <SignatureCanvas
              width={500}
              height={200}
              onSignatureChange={handleSignatureChange}
              initialSignature={null}
              className="w-full"
            />
          ) : activeTab === 'text' ? (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Enter your name above to generate a styled signature
                </p>
                {name.trim() && (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <img
                      src={signature || ''}
                      alt="Text signature preview"
                      className="max-w-full h-auto max-h-20 mx-auto"
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div 
                className={cn(
                  "border-2 border-dashed rounded-lg p-4 text-center transition-colors",
                  isDragOver 
                    ? " bg-blue-50" 
                    : "border-gray-300 hover:border-gray-400"
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="signature-upload"
                />
                <label
                  htmlFor="signature-upload"
                  className="cursor-pointer flex flex-col items-center space-y-2"
                >
                  <Upload className={cn(
                    "h-8 w-8 transition-colors",
                    isDragOver ? "text-blue-500" : "text-gray-400"
                  )} />
                  <div className="text-sm text-gray-600">
                    {isDragOver ? (
                      <span className="font-medium text-blue-600">
                        Drop image here
                      </span>
                    ) : (
                      <>
                        <span className="font-medium text-blue-600 hover:text-blue-500">
                          Click to upload
                        </span>
                        {' '}or drag and drop
                      </>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    PNG, JPG, GIF up to 5MB
                  </p>
                </label>
              </div>

              {isUploading && (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <p className="text-sm text-gray-600 mt-2">Uploading...</p>
                </div>
              )}

              {uploadedImage && (
                <div className="space-y-2">
                  <Label>Preview</Label>
                  <div className="border rounded-lg p-2 bg-gray-50">
                    <img
                      src={uploadedImage}
                      alt="Uploaded signature"
                      className="max-w-full h-auto max-h-24 mx-auto"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setUploadedImage(null);
                      setSignature(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="w-full"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Remove Image
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Signature Preview */}
          {signature && (
            <div className="space-y-2">
              <Label>Signature Preview</Label>
              <div className="border rounded-lg p-2 bg-gray-50">
                <img
                  src={signature}
                  alt="Signature preview"
                  className="max-w-full h-auto max-h-20 mx-auto"
                />
              </div>
            </div>
          )}

          {/* Stamp Image Preview */}
          {stampImage && (
            <div className="space-y-2">
              <Label>Stamp Image Preview</Label>
              <div className="border rounded-lg p-2 bg-gray-50">
                <img
                  src={stampImage}
                  alt="Stamp image preview"
                  className="max-w-full h-auto max-h-20 mx-auto"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setStampImage(null);
                  if (stampImageInputRef.current) {
                    stampImageInputRef.current.value = '';
                  }
                }}
                className="w-full"
              >
                <X className="h-4 w-4 mr-2" />
                Remove Stamp Image
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isSaveDisabled}
            className="w-full sm:w-auto"
          >
            <Check className="h-4 w-4 mr-2" />
            Save Signature
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
